import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { eventConfig } from "@/config/eventConfig";
import { termsMarkdown, privacyMarkdown } from "@/config/legalContent";

// Minimal markdown renderer: # / ## headings, - bullets, **bold**, [text](url), paragraphs.
const renderInline = (text, keyPrefix) => {
  const nodes = [];
  let remaining = text;
  let i = 0;
  const pattern = /(\*\*(.+?)\*\*)|(\[([^\]]+)\]\(([^)]+)\))/;
  let m;
  while ((m = remaining.match(pattern))) {
    const idx = m.index;
    if (idx > 0) nodes.push(remaining.slice(0, idx));
    if (m[1]) {
      nodes.push(<strong key={`${keyPrefix}-b-${i}`}>{m[2]}</strong>);
    } else if (m[3]) {
      nodes.push(
        <a key={`${keyPrefix}-a-${i}`} href={m[5]} target="_blank" rel="noopener noreferrer" className="text-[#126F6A] underline hover:text-[#0d5450]">
          {m[4]}
        </a>
      );
    }
    remaining = remaining.slice(idx + m[0].length);
    i += 1;
  }
  if (remaining) nodes.push(remaining);
  return nodes;
};

const Markdown = ({ text }) => {
  const lines = text.split("\n");
  const blocks = [];
  let list = null;
  let para = [];

  const flushPara = (k) => {
    if (para.length) {
      blocks.push(
        <p key={`p-${k}`} className="text-[#3a4a5a] leading-relaxed mb-4">
          {para.map((ln, j) => (
            <React.Fragment key={`p-${k}-${j}-${ln}`}>
              {renderInline(ln, `p-${k}-${j}`)}
              {j < para.length - 1 && <br />}
            </React.Fragment>
          ))}
        </p>
      );
      para = [];
    }
  };
  const flushList = (k) => {
    if (list) {
      blocks.push(
        <ul key={`ul-${k}`} className="list-disc pl-6 space-y-1.5 mb-5 text-[#3a4a5a] leading-relaxed">
          {list.map((it, j) => (
            <li key={`li-${k}-${j}-${it}`}>{renderInline(it, `li-${k}-${j}`)}</li>
          ))}
        </ul>
      );
      list = null;
    }
  };

  lines.forEach((raw, idx) => {
    const line = raw.replace(/\s+$/, "");
    if (line.startsWith("# ")) {
      flushPara(idx); flushList(idx);
      blocks.push(<h1 key={idx} className="text-3xl sm:text-4xl font-bold text-[#0a2138] mb-2">{line.slice(2)}</h1>);
    } else if (line.startsWith("## ")) {
      flushPara(idx); flushList(idx);
      blocks.push(<h2 key={idx} className="text-lg sm:text-xl font-bold text-[#0a2138] mt-8 mb-3">{line.slice(3)}</h2>);
    } else if (line.startsWith("- ")) {
      flushPara(idx);
      if (!list) list = [];
      list.push(line.slice(2));
    } else if (line.trim() === "") {
      flushPara(idx); flushList(idx);
    } else {
      flushList(idx);
      para.push(line);
    }
  });
  flushPara("end"); flushList("end");
  return <div>{blocks}</div>;
};

const LegalPage = ({ type }) => {
  const isTerms = type === "terms";
  const md = isTerms ? termsMarkdown : privacyMarkdown;

  useEffect(() => {
    document.title = `${isTerms ? "Terms of Use" : "Privacy Policy"} · ${eventConfig.brand.name}`;
    window.scrollTo(0, 0);
  }, [isTerms]);

  return (
    <div className="min-h-screen bg-[#FBF8F0]" data-testid={`legal-page-${type}`}>
      <header className="bg-[#071524]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex items-center justify-between">
          <img src={eventConfig.brand.logo} alt="Affinity Group logo" className="h-12 w-auto" />
          <Link to="/" data-testid="legal-back-home" className="inline-flex items-center gap-2 text-sm font-semibold text-white/80 hover:text-white transition-colors">
            <ArrowLeft size={16} /> Back to home
          </Link>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <article className="bg-white rounded-2xl border border-[rgba(10,33,56,0.1)] shadow-sm p-6 sm:p-10 text-[15px]">
          <Markdown text={md} />
        </article>
      </main>
    </div>
  );
};

export default LegalPage;
